import { ChunkEntry } from '@atlassian/bitkit-diff';

import { buildLineNumbers } from './get-context-data';

type ContextExpansionLine = {
  content: string;
  from_line: number;
  to_line: number;
};

/**
 * Helper function to create decoded lines with proper line numbers
 */
const createDecodedLines = (
  lines: string[],
  fromLineNumbers: number[],
  toLineNumbers: number[]
): ContextExpansionLine[] => {
  return lines.map((line: string, index: number) => ({
    content: ` ${line}`, // diff content starts with +, - or ' '
    from_line: fromLineNumbers[index],
    to_line: toLineNumbers[index],
  }));
};

export const buildChunk = (lines: ContextExpansionLine[]): ChunkEntry => {
  const content = `@@ -${lines[0].from_line},${lines.length} +${lines[0].to_line},${lines.length} @@`;

  return {
    id: content,
    content,
    oldStart: lines[0].from_line,
    oldLines: lines.length,
    newStart: lines[0].to_line,
    newLines: lines.length,
    extra: {
      isLoadingAbove: false,
      isLoadingBelow: false,
      before: { hasMoreLines: lines[0].from_line !== 1 },
      after: { hasMoreLines: true },
    },
    changes: lines.map((line: ContextExpansionLine) => ({
      type: 'normal',
      normal: true,
      oldLine: line.from_line,
      newLine: line.to_line,
      content: line.content,
    })),
  };
};

/**
 *
 * @param lines the context lines between the start and end chunks of a multi-line conversation
 * @param chunks the diff chunks for the PR conversation file
 * @param linesToFirstChunk the number of lines to expand above the first chunk (to the comment start)
 * @param linesToLastChunk the number of lines to expand below the last chunk (to the comment end)
 * @returns context lines chunks in between the existing diff chunks
 */
export const buildContextChunks = (
  lines: string[],
  chunks: ChunkEntry[],
  linesToFirstChunk: number | null,
  linesToLastChunk: number | null
): ChunkEntry[] => {
  const builtChunks: ChunkEntry[] = [];

  // if there are no chunks, we don't need to build any context chunks
  // this shouldn't happen based on the current caller logic but we'll handle it
  // just in case
  if (chunks.length < 1) {
    return [];
  }
  const startChunk = chunks[0];

  // expand from comment start to first MLC rendered chunk if needed
  if (linesToFirstChunk) {
    const preChunkLines = lines.slice(0, linesToFirstChunk);
    const fromLineNumbers = buildLineNumbers(
      preChunkLines.length,
      startChunk.oldStart - preChunkLines.length
    );
    const toLineNumbers = buildLineNumbers(
      preChunkLines.length,
      startChunk.newStart - preChunkLines.length
    );
    const decodedLines = createDecodedLines(
      preChunkLines,
      fromLineNumbers,
      toLineNumbers
    );
    if (decodedLines.length > 0) {
      builtChunks.push(buildChunk(decodedLines));
    }
  }

  let startIndex = linesToFirstChunk || 0;
  let startToLine = startChunk.newStart + startChunk.newLines;
  let startFromLine = startChunk.oldStart + startChunk.oldLines;

  // build context chunks in between every rendered chunk within the MLC span
  // this will ultimately let us collapse the MLC lines into a single chunk
  // since we build context chunks ending at the current iteration chunk,
  // we can start iterating from the second chunk.
  for (const currentChunk of chunks.slice(1)) {
    const contextChunkLength = currentChunk.newStart - startToLine;

    // grab the context lines until the start of the current chunk
    const contextLines = lines.slice(
      startIndex,
      startIndex + contextChunkLength
    );
    // build the to line numbers
    const toLineNumbers = buildLineNumbers(contextChunkLength, startToLine);
    // build the from line numbers
    const fromLineNumbers = buildLineNumbers(contextChunkLength, startFromLine);

    // build the chunk
    const decodedLines = createDecodedLines(
      contextLines,
      fromLineNumbers,
      toLineNumbers
    );
    if (decodedLines.length > 0) {
      builtChunks.push(buildChunk(decodedLines));
    }

    // update variables by the current chunk values to pass by it and start
    // building the next context chunk afterwards
    startToLine = currentChunk.newStart + currentChunk.newLines;
    startFromLine = currentChunk.oldStart + currentChunk.oldLines;
    startIndex += contextLines.length + currentChunk.newLines;
  }

  // expand from last MLC rendered chunk to comment end if needed
  if (linesToLastChunk) {
    const postChunkLines = lines.slice(lines.length - linesToLastChunk);
    const fromLineNumbers = buildLineNumbers(
      postChunkLines.length,
      startFromLine
    );
    const toLineNumbers = buildLineNumbers(postChunkLines.length, startToLine);
    const decodedLines = createDecodedLines(
      postChunkLines,
      fromLineNumbers,
      toLineNumbers
    );
    if (decodedLines.length > 0) {
      builtChunks.push(buildChunk(decodedLines));
    }
  }

  return builtChunks;
};
